import usePendingState from '../Hooks/usePendingState';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useNotification } from '../Contexts/NotificationContext';

function FilterSide({ isFilterOpen, setIsFilterOpen }) {
    const { setNotification } = useNotification();
    const [searchLoading, setSearchLoading, searchPendingRef] = usePendingState();
    const [searchQuery, setSearchQuery] = useState('');
    const [filtersLoading, setFiltersLoading] = useState(true);
    const [tipos, setTipos] = useState([]);
    const [formatos, setFormatos] = useState([]);
    const [tipoSelecionado, setTipoSelecionado] = useState(null);
    const [formatoSelecionado, setFormatoSelecionado] = useState(null);

    const navigate = useNavigate();
    const location = useLocation();

    const accessToken = localStorage.getItem('access_token');
    const baseUrl = process.env.REACT_APP_API_URL;

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);

        setSearchQuery(queryParams.get('q') || '');
        setTipoSelecionado(queryParams.get('tipo') || null);
        setFormatoSelecionado(queryParams.get('formato') || null);

        const fetchFiltros = async () => {
            try {
                const response = await fetch(`${baseUrl}/painel/filtros`, {
                    method: 'GET',
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                });

                if (response.ok) {
                    const data = await response.json();
                    setTipos(data.tipos || []);
                    setFormatos(data.formatos || []);
                } else {
                    throw new Error('Erro ao buscar filtros');
                }
            } catch (error) {
                setNotification({
                    type: 'error',
                    message: error.message,
                    show: true,
                });
            } finally {
                setFiltersLoading(false);
            }
        };

        fetchFiltros();
    }, []);

    const handleTypeChange = (e) => {
        setTipoSelecionado(e.target.value);
    };

    const handleFormatChange = (e) => {
        setFormatoSelecionado(e.target.value);
    };

    const handleSearchSubmit = async (e) => {
        e.preventDefault();
        if (searchPendingRef.current || filtersLoading) return;
        setSearchLoading(true);

        const params = new URLSearchParams();
        if (searchQuery) params.append('q', searchQuery);
        if (tipoSelecionado) params.append('tipo', tipoSelecionado);
        if (formatoSelecionado) params.append('formato', formatoSelecionado);

        try {
            const response = await fetch(`${baseUrl}/painel/filtros/pesquisar?${params.toString()}`, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });

            if (response.ok) {
                navigate(`/painel/pesquisar?${params.toString()}`, { replace: true });
            } else {
                throw new Error('Erro ao buscar dados');
            }
        } catch (error) {
            setNotification({
                type: 'error',
                message: error.message,
                show: true,
            });
        } finally {
            setSearchLoading(false);
        }
    };

    return (
        <div
            className={`fixed lg:max-w-[20%] lg:absolute pb-10 max-lg:transition-all max-lg:duration-300 ${isFilterOpen ? `before:right-0 h-[calc(100vh_-_5em)] 2xl:min-h-[calc(100vh_-_12.35em)] lg:h-full max-lg:translate-x-0 lg:visible border-r border-sky-100 border-opacity-30 ` : ' before:transition-all before:right-60 h-[calc(100vh_-_13.5em)] lg:h-[calc(100vh_-_14.9em)] max-lg:-translate-x-[120%] lg:invisible'} before:content-[''] z-[4] before:absolute before:top-0 before:bottom-0 before:right-0 before:w-[100vw] xl:before:w-[40vw] before:bg-primary`}
        >
            <button
                onClick={() => {
                    setIsFilterOpen(false);
                }}
                className={`bg-secondary flex items-center justify-center rounded-full rotate-45 w-6 h-6 pb-1 absolute z-[10] top-2 right-0 text-white text-xl font-medium translate-x-1/2 ${isFilterOpen ? '' : 'hidden'}`}
            >
                +
            </button>
            <div className="overflow-x-hidden overflow-y-auto no-scrollbar max-h-full relative">
                <form className="pt-8 z-[1] pr-6 pl-1" onSubmit={handleSearchSubmit}>
                    {filtersLoading && <p role="status" className="text-white">Carregando filtros...</p>}
                    <fieldset disabled={filtersLoading || searchLoading} aria-busy={filtersLoading || searchLoading}>
                    <div className="mb-8 md:mb-10">
                        <input
                            className="text-sky-100 border border-sky-100 border-opacity-30 w-full bg-transparent max-sm:text-sm py-1.5 px-4 rounded-lg"
                            placeholder="Pesquisa de arquivos"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>

                    <div className="mb-8 md:mb-10">
                        <h5 className="text-lg md:text-xl lg:text-2xl text-secondary font-bold">Materiais</h5>
                        <nav className="mt-2 md:mt-4">
                            <ul className="space-y-2 md:space-y-3">
                                {tipos.map((tipo) => (
                                    <li key={tipo.id}>
                                        <label className="flex items-center">
                                            <label className="relative flex cursor-pointer mr-1.5">
                                                <input
                                                    type="radio"
                                                    name="tipo"
                                                    value={tipo.id}
                                                    onChange={handleTypeChange}
                                                    checked={Number(tipoSelecionado) === tipo.id}
                                                    className="peer appearance-none w-4 h-4 border border-secondary rounded-full"
                                                />
                                                <span className="peer-checked:content-[''] peer-checked:absolute peer-checked:inset-[3px] rounded-full peer-checked:bg-white" />
                                            </label>
                                            <span className="max-sm:text-sm text-white font-medium">{tipo.nome}</span>
                                        </label>
                                    </li>
                                ))}
                            </ul>
                        </nav>
                    </div>

                    <div className="mb-8 md:mb-10">
                        <h5 className="text-lg md:text-xl lg:text-2xl text-secondary font-bold">Tipos de Arquivo</h5>
                        <nav className="mt-2 md:mt-4">
                            <ul className="space-y-2 md:space-y-3">
                                {formatos.map((formato) => (
                                    <li key={formato.id}>
                                        <label className="flex items-center">
                                            <label className="relative flex cursor-pointer mr-1.5">
                                                <input
                                                    type="radio"
                                                    name="formato"
                                                    value={formato.id}
                                                    onChange={handleFormatChange}
                                                    checked={Number(formatoSelecionado) === formato.id}
                                                    className="peer appearance-none w-4 h-4 border border-secondary rounded-full"
                                                />
                                                <span className="peer-checked:content-[''] peer-checked:absolute peer-checked:inset-[3px] rounded-full peer-checked:bg-white" />
                                            </label>
                                            <span className="max-sm:text-sm text-white font-medium">{formato.nome}</span>
                                        </label>
                                    </li>
                                ))}
                            </ul>
                        </nav>
                    </div>

                    <div>
                        <button
                            type="submit"
                            className="max-sm:scale-90 origin-top-left group flex items-center gap-3 px-4 py-2 border border-secondary bg-secondary bg-opacity-0 text-white font-bold uppercase tracking-wider rounded-lg transition-all hover:bg-opacity-100"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="22.525"
                                height="17.414"
                                viewBox="0 0 22.525 17.414"
                                className="fill-secondary transition-all group-hover:fill-primary"
                            >
                                <g id="Camada_1-2" transform="translate(0.262 0.25)">
                                    <path
                                        id="Caminho_177"
                                        data-name="Caminho 177"
                                        d="M11.973,16.238a2.36,2.36,0,0,1-4.232.746c-.081-.126-.309-.728-.359-.746L.625,16.231c-.826-.051-.834-1.049,0-1.1l6.757-.007c.151-.265.227-.533.405-.791a2.33,2.33,0,0,1,4.155.752l.053.039,9.336,0c.856.014.905,1.043.044,1.105l-9.4.007ZM9.564,14.4a1.287,1.287,0,0,0,.216,2.564A1.287,1.287,0,0,0,9.564,14.4Z"
                                        transform="translate(0 -1.113)"
                                        strokeWidth="0.5"
                                    />
                                    <path
                                        id="Caminho_178"
                                        data-name="Caminho 178"
                                        d="M19.1,8.445c.094.06,1.909-.023,2.227,0a.749.749,0,0,1,.53.207.512.512,0,0,1-.3.854c-.348.041-2.365-.077-2.458,0a2.357,2.357,0,0,1-4.59-.007L.291,9.475C-.269,9.105.047,8.441.669,8.45l13.806,0a.113.113,0,0,0,.092-.045c.047-.057.2-.51.3-.669a2.346,2.346,0,0,1,4.241.708Zm-2.456-.718a1.291,1.291,0,0,0,.216,2.566A1.291,1.291,0,1,0,16.648,7.727Z"
                                        transform="translate(0 -0.556)"
                                        strokeWidth="0.5"
                                    />
                                    <path
                                        id="Caminho_179"
                                        data-name="Caminho 179"
                                        d="M8.655,1.774H21.331c.622-.009.938.656.377,1.025L8.768,2.834l-.093.045A2.341,2.341,0,0,1,4.9,4.122a3.405,3.405,0,0,1-.836-1.281L.358,2.825c-.616-.285-.376-1.033.267-1.047l3.515,0a2.324,2.324,0,0,1,4.515,0ZM6.25,1.052a1.277,1.277,0,1,0,.719,2.421A1.277,1.277,0,0,0,6.25,1.052Z"
                                        transform="translate(0 -0.001)"
                                        strokeWidth="0.5"
                                    />
                                </g>
                            </svg>
                            {searchLoading ? 'Pesquisando...' : 'Filtrar'}
                        </button>
                    </div>
                    </fieldset>
                </form>
            </div>
        </div>
    );
}

export default FilterSide;
